import { useState } from "react";
import i18next from "../../i18n";
import { BIDDING_TYPES } from "../../utils";
import { useCreateMultipleOrder, useCreateOrder } from "../../hooks/useOrders";
import { buildOrderPayload } from "./buildOrderPayload";

type OrderSubmissionArgs = {
  ordersToSubmit: any[];
  generalBidData: any;
  selectedType: any;
  selectedComplexType: any;
  user: any;
  invites: any[];
  cleanUserInvites: () => void;
  createOrderInvitation: (emailsList: any, orderId: any) => void;
  openNotification: (message: string) => void;
  openError: (error: unknown) => void;
  navigate: (to: string) => void;
  closeDialog: () => void;
};

/**
 * The create-order mutations + the launch orchestration (single vs joint vs
 * one-order-per-line), lifted verbatim from Order.tsx. The payload itself comes
 * from `buildOrderPayload` (the byte-equivalent SSOT). Reusable by Task 6.
 */
export const useOrderSubmission = ({
  ordersToSubmit,
  generalBidData,
  selectedType,
  selectedComplexType,
  user,
  invites,
  cleanUserInvites,
  createOrderInvitation,
  openNotification,
  openError,
  navigate,
  closeDialog,
}: OrderSubmissionArgs) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creatingIndividualOrders, setCreatingIndividualOrders] =
    useState(false);
  const [individualOrdersCompleted, setIndividualOrdersCompleted] = useState(0);
  const [totalIndividualOrders, setTotalIndividualOrders] = useState(0);
  const [pendingOrderInvitations, setPendingOrderInvitations] = useState<any[]>(
    []
  );

  const orderInvitationHandler = (isMultiple: boolean, data: any) => {
    if (invites.length === 0) return;
    if (isMultiple) {
      createOrderInvitation(invites[0].emailsList, data[0].id);
    } else {
      createOrderInvitation(invites[0].emailsList, data.id);
    }
    cleanUserInvites();
  };

  const { mutate: createOrder, isPending: isCreatingOrder } = useCreateOrder(
    (res) => {
      openNotification(i18next.t("tenders.success"));

      if (selectedComplexType?.value !== BIDDING_TYPES.JOINT) {
        if (creatingIndividualOrders) {
          const newCompletedCount = individualOrdersCompleted + 1;
          setIndividualOrdersCompleted(newCompletedCount);

          const correspondingOrderData = pendingOrderInvitations.find(
            (orderData) => orderData.material.id === res.material.id
          );

          if (correspondingOrderData) {
            const emailInvitations = correspondingOrderData.suppliers.filter(
              (sup: any) => sup.emailsList && !sup.id
            );
            emailInvitations.forEach((invitation: any) => {
              createOrderInvitation(invitation.emailsList, res.id);
            });
          }

          if (newCompletedCount >= totalIndividualOrders) {
            setCreatingIndividualOrders(false);
            setIndividualOrdersCompleted(0);
            setTotalIndividualOrders(0);
            setPendingOrderInvitations([]);
            cleanUserInvites();
            return navigate("/orders");
          }
          return;
        } else {
          const orderEmailInvitations = ordersToSubmit.find((order) =>
            order.suppliers.some((sup: any) => sup.emailsList && !sup.id)
          );
          if (orderEmailInvitations) {
            const emailInvitations = orderEmailInvitations.suppliers.filter(
              (sup: any) => sup.emailsList && !sup.id
            );
            emailInvitations.forEach((invitation: any) => {
              createOrderInvitation(invitation.emailsList, res.id);
            });
          }
        }
      } else {
        orderInvitationHandler(false, res);
      }

      return navigate("/orders");
    },
    (error) => {
      setIsSubmitting(false);
      openError(error);
    }
  );

  const { mutate: createMultipleOrder, isPending: isCreatingMultipleOrder } =
    useCreateMultipleOrder(
      (res) => {
        openNotification(i18next.t("tenders.success"));
        orderInvitationHandler(true, res);
        return navigate("/orders");
      },
      (error) => {
        setIsSubmitting(false);
        openError(error);
      }
    );

  const onSubmit = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    closeDialog();

    const dataToSubmit = buildOrderPayload({
      ordersToSubmit,
      generalBidData,
      selectedType,
      selectedComplexType,
      user,
    });

    if (selectedComplexType.value === BIDDING_TYPES.JOINT) {
      createMultipleOrder(dataToSubmit);
    } else {
      if (dataToSubmit.length === 1) {
        createOrder(dataToSubmit[0]);
      } else {
        setCreatingIndividualOrders(true);
        setIndividualOrdersCompleted(0);
        setTotalIndividualOrders(dataToSubmit.length);
        setPendingOrderInvitations(dataToSubmit);
        dataToSubmit.forEach((orderData) => createOrder(orderData));
      }
    }
  };

  const onError = () => {
    setIsSubmitting(false);
    closeDialog();
  };

  return {
    isSubmitting,
    isCreatingOrder,
    isCreatingMultipleOrder,
    creatingIndividualOrders,
    onSubmit,
    onError,
  };
};
